import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume1, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";

const TranscriptAudioPlayer = forwardRef(({
  audioUrl,
  isDark,
  duration: initialDuration,
  onTimeUpdate,
  onPlayingChange,
  onDurationChange,
  preload = "metadata",
  onLoadStart,
  onWaiting,
  onCanPlay,
  onCanPlayThrough,
  onMediaError,
  onBufferUpdate,
  statusLine,
}, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [bufferedRatio, setBufferedRatio] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  const refreshBuffered = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const d = audio.duration;
    const ranges = audio.buffered;
    let end = 0;
    for (let i = 0; i < ranges.length; i += 1) {
      end = Math.max(end, ranges.end(i));
    }
    if (!d || !Number.isFinite(d) || d <= 0) {
      setBufferedRatio(0);
      onBufferUpdate?.(0, end);
      return;
    }
    const ratio = Math.min(1, end / d);
    setBufferedRatio(ratio);
    onBufferUpdate?.(ratio, end);
  }, [onBufferUpdate]);

  useImperativeHandle(ref, () => ({
    seekTo(seconds) {
      if (!audioRef.current || seconds == null) return;
      const next = Math.max(0, Math.min(duration || audioRef.current.duration || seconds, seconds));
      audioRef.current.currentTime = next;
      setCurrentTime(next);
      onTimeUpdate?.(next);
    },
    play() {
      audioRef.current?.play()?.catch(() => {});
    },
    pause() {
      audioRef.current?.pause();
    },
    getCurrentTime() {
      return audioRef.current?.currentTime ?? currentTime;
    },
  }));

  const setPlaying = (playing) => {
    setIsPlaying(playing);
    onPlayingChange?.(playing);
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    try {
      await audioRef.current.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    setCurrentTime(t);
    onTimeUpdate?.(t);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    const d = audioRef.current.duration;
    setDuration(d);
    onDurationChange?.(d);
    refreshBuffered();
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current && duration > 0) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  const skip = (seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
  };

  const formatTime = (time) => {
    if (!time || Number.isNaN(time)) return "0:00";
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const changePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) audioRef.current.playbackRate = nextRate;
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const playRatio = duration > 0 ? currentTime / duration : 0;

  return (
    <div className="space-y-2.5">
      {statusLine ? (
        <p className={`text-xs leading-snug ${isDark ? "text-slate-400" : "text-gray-600"}`}>{statusLine}</p>
      ) : null}

      <div
        className={`relative h-2 rounded-full cursor-pointer overflow-hidden ${isDark ? "bg-slate-700" : "bg-gray-200"}`}
        onClick={handleSeek}
      >
        <div
          className={`absolute inset-y-0 left-0 rounded-full ${isDark ? "bg-slate-500/70" : "bg-gray-400/60"}`}
          style={{ width: `${bufferedRatio * 100}%` }}
          title={bufferedRatio > 0 ? `Tampon: %${Math.round(bufferedRatio * 100)}` : undefined}
        />
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-[width] duration-150"
          style={{ width: `${playRatio * 100}%` }}
        />
      </div>

      <div className="flex justify-between text-[11px] font-mono">
        <span className={isDark ? "text-slate-400" : "text-gray-500"}>{formatTime(currentTime)}</span>
        <span className={isDark ? "text-slate-400" : "text-gray-500"}>
          {duration > 0 ? formatTime(duration) : "—:—"}
          {duration > 0 && bufferedRatio > 0 && bufferedRatio < 0.995 ? (
            <span className={`ml-1.5 ${isDark ? "text-violet-400" : "text-violet-600"}`}>
              · %{Math.round(bufferedRatio * 100)} tampon
            </span>
          ) : null}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" onClick={() => skip(-10)} className={`h-8 w-8 p-0 ${isDark ? "text-slate-300 hover:text-white" : ""}`}>
            <SkipBack className="w-4 h-4" />
          </Button>
          <Button
            onClick={togglePlay}
            className={`h-9 w-9 rounded-full p-0 ${isDark ? "bg-emerald-600 hover:bg-emerald-500" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => skip(10)} className={`h-8 w-8 p-0 ${isDark ? "text-slate-300 hover:text-white" : ""}`}>
            <SkipForward className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={toggleMute} className={`h-8 w-8 p-0 ${isDark ? "text-slate-300" : ""}`}>
              {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume1 className="w-4 h-4" />}
            </Button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setVolume(val);
                setIsMuted(val === 0);
                if (audioRef.current) audioRef.current.volume = val;
              }}
              className="w-14 h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={changePlaybackRate}
            className={`h-8 px-2 text-xs ${isDark ? "border-slate-700 text-slate-300" : ""}`}
          >
            {playbackRate}x
          </Button>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={audioUrl}
        preload={preload}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onProgress={refreshBuffered}
        onLoadStart={onLoadStart}
        onWaiting={onWaiting}
        onCanPlay={() => {
          refreshBuffered();
          onCanPlay?.();
        }}
        onCanPlayThrough={() => {
          refreshBuffered();
          onCanPlayThrough?.();
        }}
        onError={onMediaError}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="hidden"
      />
    </div>
  );
});

TranscriptAudioPlayer.displayName = "TranscriptAudioPlayer";

export default TranscriptAudioPlayer;
